import axiosInstance from "api"
import { Endpoint } from "api/enpoints.types"
import { classesStudentsQueryKey } from "api/reactQuery/queries/classesStudents"
import {
  groupFilterQueryKey,
  groupsQueryKey,
} from "api/reactQuery/queries/groups"
import { AxiosError, AxiosResponse } from "axios"
import { useMutation, useQueryClient } from "react-query"
import store from "store"
import { setClassList, setPage } from "store/utility/utility.slice"
import { IGroup, IGroupFilter, IGroupImportResult } from "ts/interfaces/Group"

import {
  IBulkDeleteGroupsArgs,
  IBulkDeleteGroupsVariables,
  ICreateGroupArgs,
  ICreateGroupTransactionsArgs,
  ICreateGroupTransactionsVariables,
  ICreateGroupVariables,
  IDeleteGroupArgs,
  IDeleteGroupVariables,
  IImportGroupsArgs,
  IImportGroupsVariables,
  ISaveGroupFilterArgs,
  ISaveGroupFilterVariables,
  IUpdateGroupArgs,
  IUpdateGroupVariables,
} from "./groups.types"

const groupUrl = (classId: number) =>
  Endpoint.CLASSES_ID_GROUPS.replace("CLASSID", `${classId}`)

const groupFilterUrl = (classId: number) =>
  Endpoint.CLASSES_ID_GROUPS_FILTER.replace("CLASSID", `${classId}`)

const groupIdUrl = (classId: number, groupId: number) =>
  Endpoint.CLASSES_ID_GROUPS_GROUPID.replace("CLASSID", `${classId}`).replace(
    "GROUPID",
    `${groupId}`,
  )

const groupTransactionsUrl = (classId: number, groupId: number) =>
  Endpoint.CLASSES_ID_GROUPS_GROUPID_TRANSACTIONS.replace(
    "CLASSID",
    `${classId}`,
  ).replace("GROUPID", `${groupId}`)

const bankerGroupTransactionsUrl = (classId: number, groupId: number) =>
  Endpoint.CLASSES_ID_HELPER_BANKER_GROUPS_GROUPID_TRANSACTIONS.replace(
    "ID",
    `${classId}`,
  ).replace("GROUPID", `${groupId}`)

const groupImportUrl = (classId: number) =>
  Endpoint.CLASSES_ID_GROUPS_IMPORT.replace("CLASSID", `${classId}`)

// The class Students table caches its roster in Redux (`classList`), mirrored there from
// this query's `onSuccess` — not read from react-query's `data` directly — and that query
// runs with `cacheTime: 0`, so it's dropped from the react-query cache the instant the
// table unmounts (e.g. navigating to the Groups tab to edit membership). A plain
// `invalidateQueries` has nothing left to mark stale by the time the table remounts.
// Clearing `classList` directly is the one signal that survives the unmount — the table's
// mount effect reacts to an empty `classList` by refetching.
const resetClassesStudentsCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  classId: number,
) => {
  queryClient.invalidateQueries(classesStudentsQueryKey(classId))
  // Dispatched straight to the store singleton, not via `useAppDispatch()` — this file has
  // no guarantee of running under a <Provider> (e.g. existing tests render these mutations'
  // consumers without one), and the app's one real store is this same singleton regardless.
  store.dispatch(setPage(0))
  store.dispatch(setClassList([]))
}

export const useMutationCreateGroup = (args?: ICreateGroupArgs) => {
  const queryClient = useQueryClient()

  return useMutation<AxiosResponse<IGroup>, AxiosError, ICreateGroupVariables>(
    async ({ classId, data }) =>
      await axiosInstance.post(groupUrl(classId), data),
    {
      ...args?.options,
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries(groupsQueryKey(variables.classId))
        resetClassesStudentsCache(queryClient, variables.classId)
        args?.options?.onSuccess?.(response, variables, context)
      },
    },
  )
}

export const useMutationUpdateGroup = (args?: IUpdateGroupArgs) => {
  const queryClient = useQueryClient()

  return useMutation<AxiosResponse<IGroup>, AxiosError, IUpdateGroupVariables>(
    async ({ classId, groupId, data }) =>
      await axiosInstance.patch(groupIdUrl(classId, groupId), data),
    {
      ...args?.options,
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries(groupsQueryKey(variables.classId))
        resetClassesStudentsCache(queryClient, variables.classId)
        args?.options?.onSuccess?.(response, variables, context)
      },
    },
  )
}

export const useMutationDeleteGroup = (args?: IDeleteGroupArgs) => {
  const queryClient = useQueryClient()

  return useMutation<AxiosResponse<null>, AxiosError, IDeleteGroupVariables>(
    async ({ classId, groupId }) =>
      await axiosInstance.delete(groupIdUrl(classId, groupId)),
    {
      ...args?.options,
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries(groupsQueryKey(variables.classId))
        resetClassesStudentsCache(queryClient, variables.classId)
        args?.options?.onSuccess?.(response, variables, context)
      },
    },
  )
}

export const useMutationBulkDeleteGroups = (args?: IBulkDeleteGroupsArgs) => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<null>,
    AxiosError,
    IBulkDeleteGroupsVariables
  >(
    async ({ classId, group_ids }) =>
      await axiosInstance.delete(groupUrl(classId), {
        data: { group_ids },
      }),
    {
      ...args?.options,
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries(groupsQueryKey(variables.classId))
        resetClassesStudentsCache(queryClient, variables.classId)
        args?.options?.onSuccess?.(response, variables, context)
      },
    },
  )
}

export const useMutationCreateGroupTransactions = (
  args?: ICreateGroupTransactionsArgs,
) =>
  useMutation<
    AxiosResponse<null>,
    AxiosError,
    ICreateGroupTransactionsVariables
  >(
    async ({ classId, groupId, data }) =>
      await axiosInstance.post(groupTransactionsUrl(classId, groupId), data),
    args?.options,
  )

export const useMutationCreateBankerGroupTransactions = (
  args?: ICreateGroupTransactionsArgs,
) =>
  useMutation<
    AxiosResponse<null>,
    AxiosError,
    ICreateGroupTransactionsVariables
  >(
    async ({ classId, groupId, data }) =>
      await axiosInstance.post(
        bankerGroupTransactionsUrl(classId, groupId),
        data,
      ),
    args?.options,
  )

export const useMutationImportGroups = (args?: IImportGroupsArgs) => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<IGroupImportResult>,
    AxiosError,
    IImportGroupsVariables
  >(
    async ({ classId, file, onInvalidRows, addMissingStudents, dryRun }) => {
      const formData = new FormData()
      formData.append("file", file)
      const params = {
        ...(onInvalidRows && { on_invalid_rows: onInvalidRows }),
        ...(addMissingStudents && { add_missing_students: true }),
        ...(dryRun && { dry_run: true }),
      }
      return await axiosInstance.post(groupImportUrl(classId), formData, {
        params: Object.keys(params).length ? params : undefined,
      })
    },
    {
      ...args?.options,
      onSuccess: (response, variables, context) => {
        const wasAbortedDryRun =
          variables.onInvalidRows === "abort" &&
          response.data.has_blocking_errors
        if (!response.data.dry_run && !wasAbortedDryRun) {
          queryClient.invalidateQueries(groupsQueryKey(variables.classId))
          resetClassesStudentsCache(queryClient, variables.classId)
          if (response.data.enrolled.length > 0) {
            queryClient.invalidateQueries(["classes"])
          }
        }
        args?.options?.onSuccess?.(response, variables, context)
      },
    },
  )
}

export const useMutationSaveGroupFilter = (args?: ISaveGroupFilterArgs) => {
  const queryClient = useQueryClient()

  return useMutation<
    AxiosResponse<IGroupFilter>,
    AxiosError,
    ISaveGroupFilterVariables
  >(
    async ({ classId, data }) =>
      await axiosInstance.put(groupFilterUrl(classId), data),
    {
      ...args?.options,
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries(groupFilterQueryKey(variables.classId))
        args?.options?.onSuccess?.(response, variables, context)
      },
    },
  )
}
